import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import cn from 'classnames';
import { t } from 'i18next';
import { Button } from 'primereact/button';
import { useRef, useEffect, useCallback, type FormEvent } from 'react';
import { FormProvider, SubmitHandler, useForm, UseFormSetError, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AllowAccess } from '@features/auth/ControlAccess/AllowAccess/AllowAccess';
import { useControlAccess } from '@features/auth/hooks/useControlAccess.ts';
import { displayApiErrorsOnFormFields } from '@features/campaigns/builder/utils/displayApiErrorsOnFormFields.ts';
import { FieldAgency } from '@features/campaigns/details/components/FormFields/BillingForm/FieldAgency/FieldAgency.tsx';
import { FieldAuthorizedBudget } from '@features/campaigns/details/components/FormFields/BillingForm/FieldAuthorizedBudget/FieldAuthorizedBudget';
import { FieldBillingContact } from '@features/campaigns/details/components/FormFields/BillingForm/FieldBillingContact/FieldBillingContact.tsx';
import { FieldPurchaseOrder } from '@features/campaigns/details/components/FormFields/BillingForm/FieldPurchaseOrder/FieldPurchaseOrder.tsx';
import { FieldRealBudget } from '@features/campaigns/details/components/FormFields/BillingForm/FieldRealBudget/FieldRealBudget.tsx';
import { BillingFormFieldTypes } from '@features/campaigns/details/components/FormFields/FieldTypes.ts';
import AuthorizedBudgetReadOnly from '@features/campaigns/details/containers/BillingDetails/AuthorizedBudgetReadOnly/AuthorizedBudgetReadOnly';
import {
  AuthorizedBudgetPayload,
  BillingDetailsData,
  prepareBillingDetailsPayload,
  RealBudgetPayload,
  restoreBillingDetailsFormValues,
} from '@features/campaigns/details/containers/BillingDetails/BillingDetails.dto.ts';
import {
  billingDetailsValidationSchema,
  BillingDetailsValues,
} from '@features/campaigns/details/containers/BillingDetails/BillingDetails.types.ts';
import { updateAuthorizedBudget } from '@features/campaigns/details/services/authorizeBudget';
import { updateBillingDetails } from '@features/campaigns/details/services/billingDetails.ts';
import { updateRealBudget } from '@features/campaigns/details/services/realBudget.ts';
import { Spinner } from '@features/campaigns/shared/components/Spinner/Spinner.tsx';
import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys.ts';
import {
  CampaignStatus,
  CampaignStatuses,
  CampaignType,
  CampaignTypes,
} from '@features/campaigns/shared/types/campaign.ts';
import { BillingDetailsPayload } from '@features/campaigns/shared/types/campaignDetails.ts';
import { AccessRight, AccessRights } from '@shared/api/commonApiInterfaces.ts';
import { LeaveGuardModal } from '@shared/components/Modal/LeaveGuardModal/LeaveGuardModal.tsx';
import { Item } from '@shared/components/Review/Item/Item';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage.tsx';
import { ErrorStatus } from '@shared/consts/errorStatus';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { Permissions } from '@shared/consts/permissions.ts';
import { useLeaveGuardModalContext } from '@shared/providers/LeaveGuardModalProvider/useLeaveGuardModalContext.ts';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { routePaths } from '@shared/router/routePaths.ts';
import { CampaignId } from '@shared/types/campaign.ts';
import { isApiError } from '@shared/types/typeGuards';
import { formatNameWithCode } from '@shared/utils/formatNameWithCode.ts';
import { logError } from '@shared/utils/logError.ts';
import { navigateTo } from '@shared/utils/navigateTo.tsx';

import styles from './BillingDetails.module.scss';

interface BillingDetailsProps {
  data: BillingDetailsData;
  accessRights: AccessRight[];
  campaignStatus: CampaignStatus;
  campaignType: CampaignType;
  isOnboardingCompleted: boolean;
}

const handleCreditCheckError = (setError: UseFormSetError<BillingDetailsPayload>, error: unknown) => {
  if (isAxiosError(error) && isApiError(error)) {
    const apiErrorStatus = error.response?.data?.status;

    if (apiErrorStatus === ErrorStatus.CREDIT_CHECK_FAILED) {
      setError(BillingFormFieldTypes.BILLING_CONTACT, {
        type: 'manual',
        message: t('api.error.custom.creditCheck', {
          ns: i18nNameSpace.API,
        }),
      });
    }
  }
};

const editBillingDetailsAccessRights: AccessRight[] = [
  AccessRights.EDIT_AGENCY,
  AccessRights.EDIT_AUTHORIZED_BUDGET,
  AccessRights.EDIT_BILLING_CONTACT,
  AccessRights.EDIT_PURCHASE_ORDER,
  AccessRights.EDIT_REAL_BUDGET,
];

const checkIfHasAnyEditBillingDetailsAccessRight = (accessRights: AccessRight[]) => {
  return editBillingDetailsAccessRights.some((editBillingDetailsAccessRight) =>
    accessRights.includes(editBillingDetailsAccessRight),
  );
};

export default function BillingDetails({
  data,
  accessRights,
  campaignStatus,
  campaignType,
  isOnboardingCompleted,
}: BillingDetailsProps) {
  const { toggleModal, setIsFormDirty, isOpen: isLeaveBuilderModalOpen } = useLeaveGuardModalContext();
  const navigate = useNavigate();
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { campaignId } = useParams();
  const { state: locationState } = useLocation();
  const queryClient = useQueryClient();
  const { agency, billingContact, purchaseOrder } = data;
  const formValues = restoreBillingDetailsFormValues(data);
  const hasAnyEditBillingDetailsAccessRight = checkIfHasAnyEditBillingDetailsAccessRight(accessRights);
  const authorizedBudgetPrevValue = useRef(formValues.authorizedBudget);
  const realBudgetPrevValue = useRef(formValues.realBudget);
  const { isExternalDirectClient, isExternalAgency } = useUserDetails();
  const { isUserAllowedToUseResource: isAllowedToEditRealBudgetField } = useControlAccess({
    requiredPermissions: [Permissions.CAMPAIGN_UPDATE_REAL_BUDGET_ALL_STATUSES],
    additionalCondition: accessRights.includes(AccessRights.EDIT_REAL_BUDGET),
  });

  const isCurrentUserExternal = isExternalDirectClient || isExternalAgency;
  const isFormHidden = isCurrentUserExternal
    ? campaignStatus === CampaignStatuses.DRAFT && Boolean(!data.billingContact?.name)
    : false;

  const canInternalUserModifyCampaignBillingDetails =
    campaignType === CampaignTypes.SELF_SERVICE ? isOnboardingCompleted : true;

  const form = useForm<BillingDetailsValues>({
    defaultValues: formValues,
    resolver: zodResolver(billingDetailsValidationSchema),
  });

  const canEditAgency = accessRights.includes(AccessRights.EDIT_AGENCY);
  const canEditPurchaseOrder = accessRights.includes(AccessRights.EDIT_PURCHASE_ORDER);

  const authorizedBudgetFieldCurrentValue = useWatch({
    control: form.control,
    name: BillingFormFieldTypes.AUTHORIZED_BUDGET,
  });
  const realBudgetFieldCurrentValue = useWatch({
    control: form.control,
    name: BillingFormFieldTypes.REAL_BUDGET,
  });

  const agencyNameWithCode = formatNameWithCode(agency?.name, agency?.code);
  const billingContactValue = isExternalDirectClient
    ? billingContact?.name
    : formatNameWithCode(billingContact?.name, billingContact?.code);

  const canSubmitForm =
    (hasAnyEditBillingDetailsAccessRight || isAllowedToEditRealBudgetField) &&
    !isExternalDirectClient &&
    Boolean(data.advertiserId);

  const resetFormDirtyState = () => {
    const values = form.getValues();
    form.reset(values, { keepDirtyValues: true });
  };

  const navigateToListOrDetails = () => {
    void navigateTo(navigate, locationState || routePaths.campaigns).catch(logError);
  };
  const updateBillingDetailsValidationFailCallback = useCallback(() => {
    toggleModal();
  }, [toggleModal]);

  const { mutate: updateBillingDetailsMutation, isPending: updateBillingDetailsIsPending } = useMutation({
    mutationFn: ({ payload, campaignId }: { payload: BillingDetailsPayload; campaignId: CampaignId }) =>
      updateBillingDetails(payload, campaignId),
    onError: (error) => {
      handleCreditCheckError(form.setError, error);
      displayApiErrorsOnFormFields(form.setError, error);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS] });
      toast.success(
        <SuccessMessage message={t('api.success.campaign.billingDetailsSaved', { ns: i18nNameSpace.API })} />,
      );

      const shouldResetAuthorizedBudget =
        authorizedBudgetPrevValue.current === authorizedBudgetFieldCurrentValue &&
        Boolean(authorizedBudgetFieldCurrentValue);

      if (shouldResetAuthorizedBudget) {
        authorizedBudgetPrevValue.current = data?.authorizedBudget;
      }

      if (isLeaveBuilderModalOpen) {
        navigateToListOrDetails();
      }

      resetFormDirtyState();
    },
  });

  const { mutate: updateAuthorizedBudgetMutation, isPending: updateAuthorizedBudgetIsPending } = useMutation({
    mutationFn: ({ payload, campaignId }: { payload: AuthorizedBudgetPayload; campaignId: CampaignId }) =>
      updateAuthorizedBudget(payload, campaignId),
    onError: (error) => {
      displayApiErrorsOnFormFields(form.setError, error);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS] });
      toast.success(
        <SuccessMessage message={t('api.success.campaign.authorizedBudgetUpdated', { ns: i18nNameSpace.API })} />,
      );
      resetFormDirtyState();
      authorizedBudgetPrevValue.current = data?.authorizedBudget;

      if (isLeaveBuilderModalOpen) {
        navigateToListOrDetails();
      }
    },
  });

  const { mutate: updateRealBudgetMutation, isPending: updateRealBudgetIsPending } = useMutation({
    mutationFn: ({ payload, campaignId }: { payload: RealBudgetPayload; campaignId: CampaignId }) =>
      updateRealBudget(payload, campaignId),
    onError: (error) => {
      displayApiErrorsOnFormFields(form.setError, error);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS] });
      toast.success(
        <SuccessMessage message={t('api.success.campaign.realBudgetUpdated', { ns: i18nNameSpace.API })} />,
      );
      resetFormDirtyState();
      realBudgetPrevValue.current = data?.realBudget;

      if (isLeaveBuilderModalOpen) {
        navigateToListOrDetails();
      }
    },
  });

  const clearAgencyName = () => {
    form.setValue(BillingFormFieldTypes.AGENCY, undefined);
  };

  const clearBillingContactName = () => {
    form.setValue(BillingFormFieldTypes.BILLING_CONTACT, '');
  };

  const submitForm: SubmitHandler<BillingDetailsValues> = useCallback(
    ({ authorizedBudget, realBudget, ...billingDetailsPayload }) => {
      if (!campaignId) {
        return null;
      }

      const fieldsToUpdate = form.formState.dirtyFields;
      const shouldUpdateAuthorizedBudget = authorizedBudgetPrevValue.current !== authorizedBudgetFieldCurrentValue;
      const shouldUpdateRealBudget = realBudgetPrevValue.current !== realBudgetFieldCurrentValue;

      if (fieldsToUpdate.agencyId || fieldsToUpdate.billingContactId || fieldsToUpdate.purchaseOrder) {
        const payload = prepareBillingDetailsPayload(billingDetailsPayload, canEditAgency, canEditPurchaseOrder);

        updateBillingDetailsMutation({ payload, campaignId });
      }

      if (fieldsToUpdate.authorizedBudget && authorizedBudget && shouldUpdateAuthorizedBudget) {
        updateAuthorizedBudgetMutation({ payload: { authorizedBudget }, campaignId });
      }

      if (fieldsToUpdate.realBudget && shouldUpdateRealBudget) {
        updateRealBudgetMutation({ payload: { realBudget: realBudget ?? null }, campaignId });
      }
    },
    [
      campaignId,
      form,
      authorizedBudgetFieldCurrentValue,
      realBudgetFieldCurrentValue,
      canEditAgency,
      canEditPurchaseOrder,
      updateBillingDetailsMutation,
      updateAuthorizedBudgetMutation,
      updateRealBudgetMutation,
    ],
  );

  const updateBillingDetailsAndQuit = useCallback(
    (formSubmitData: BillingDetailsValues) => {
      submitForm(formSubmitData);
    },
    [submitForm],
  );

  const handleBillingFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      void form.handleSubmit(submitForm)(event);
    },
    [form, submitForm],
  );

  const handleLeaveGuardSaveSubmit = useCallback(() => {
    void form.handleSubmit(updateBillingDetailsAndQuit, updateBillingDetailsValidationFailCallback)();
  }, [form, updateBillingDetailsAndQuit, updateBillingDetailsValidationFailCallback]);

  const submitDisabled =
    !form.formState.isDirty ||
    updateBillingDetailsIsPending ||
    updateAuthorizedBudgetIsPending ||
    updateRealBudgetIsPending;

  useEffect(() => {
    setIsFormDirty(form.formState.isDirty);
    return () => setIsFormDirty(false);
  }, [form.formState.isDirty]);

  if (!campaignId || isFormHidden) {
    return null;
  }

  return (
    <>
      <FormProvider {...form}>
        <form className={styles.wrapper} onSubmit={handleBillingFormSubmit} autoComplete={'off'} role='form'>
          {(updateBillingDetailsIsPending || updateAuthorizedBudgetIsPending) && <Spinner variant='elementOverlay' />}
          <div className='grid'>
            <div className='row'>
              <div className='col-12'>
                <hr className={styles.divider} />
              </div>
            </div>
            <div className='row'>
              <div className='col-12'>
                <AllowAccess
                  fallbackView={
                    accessRights.includes(AccessRights.READ_AGENCY) && (
                      <Item label={t('page.billingForm.agencyField.label')} value={agencyNameWithCode || '-'} />
                    )
                  }
                  additionalCondition={
                    accessRights.includes(AccessRights.EDIT_AGENCY) &&
                    Boolean(data.advertiserId) &&
                    canInternalUserModifyCampaignBillingDetails
                  }
                >
                  <FieldAgency clearSelectedValuesHandler={clearAgencyName} restoredInputValue={agencyNameWithCode} />
                </AllowAccess>
              </div>
            </div>
            <div className='row'>
              <div className='col-12'>
                <AllowAccess
                  fallbackView={
                    accessRights.includes(AccessRights.READ_BILLING_CONTACT) && (
                      <Item
                        label={t('page.billingForm.billingContactField.label')}
                        value={billingContactValue || '-'}
                      />
                    )
                  }
                  additionalCondition={
                    accessRights.includes(AccessRights.EDIT_BILLING_CONTACT) &&
                    Boolean(data.advertiserId) &&
                    !isExternalDirectClient
                  }
                >
                  <FieldBillingContact
                    clearSelectedValuesHandler={clearBillingContactName}
                    restoredInputValue={billingContactValue}
                    advertiserId={data.advertiserId}
                    campaignId={campaignId}
                  />
                </AllowAccess>
              </div>
            </div>
            <div className='row'>
              <div className='col-12'>
                <AllowAccess
                  fallbackView={
                    accessRights.includes(AccessRights.READ_PURCHASE_ORDER) && (
                      <Item label={t('page.billingForm.orderField.label')} value={purchaseOrder || '-'} />
                    )
                  }
                  additionalCondition={
                    accessRights.includes(AccessRights.EDIT_PURCHASE_ORDER) &&
                    Boolean(data.advertiserId) &&
                    canInternalUserModifyCampaignBillingDetails
                  }
                >
                  <FieldPurchaseOrder />
                </AllowAccess>
              </div>
            </div>
          </div>

          <div className='grid'>
            <div className='row'>
              <div className='col-12'>
                <hr className={cn(styles.divider, styles.noMargins)} />
              </div>
            </div>

            <AllowAccess
              fallbackView={
                accessRights.includes(AccessRights.READ_AUTHORIZED_BUDGET) && (
                  <AuthorizedBudgetReadOnly authorizedBudget={formValues.authorizedBudget} />
                )
              }
              additionalCondition={accessRights.includes(AccessRights.EDIT_AUTHORIZED_BUDGET)}
            >
              <div className='row'>
                <div className='col-12'>
                  <FieldAuthorizedBudget />
                </div>
              </div>
            </AllowAccess>

            <AllowAccess
              requiredPermissions={[Permissions.CAMPAIGN_UPDATE_REAL_BUDGET_ALL_STATUSES]}
              additionalCondition={isAllowedToEditRealBudgetField}
            >
              <div className='row'>
                <div className='col-12'>
                  <FieldRealBudget />
                </div>
              </div>
            </AllowAccess>
          </div>

          {canSubmitForm && (
            <div className={styles.footer}>
              <Button data-testid='submit-billing-form' type='submit' outlined disabled={submitDisabled}>
                {t('page.billingForm.common.save')}
              </Button>
            </div>
          )}
        </form>
      </FormProvider>
      <LeaveGuardModal
        cancelCallback={() => navigateToListOrDetails()}
        submitCallback={handleLeaveGuardSaveSubmit}
        headerText={t('page.campaignDetails.modals.headers.leaveCampaignDetails')}
        cancelLabelText={t('page.campaignDetails.modals.discardChanges')}
        submitLabelText={t('page.campaignDetails.modals.yesSave')}
        submitIsLoading={submitDisabled}
      >
        {t('page.campaignDetails.modals.content.leaveCampaignDetails')}
      </LeaveGuardModal>
    </>
  );
}
